// src/pages/settings/settings/SmsConfiguration.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Space, Statistic, Typography } from "antd";
import { MessageOutlined, ShoppingCartOutlined, SaveOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import axios from "axios";
import dayjs from "dayjs";

import { DataTable, SectionCard, StatusTag } from "../../../components/shared";
import { useNotifications } from "../../../context/NotificationContext";
import { extractValidationErrors } from "../../../utils/apiError";
import {
	fetchSmsCredits,
	fetchSmsMessages,
	fetchSmsNumbers,
	fetchSmsSettings,
	requestSmsCredits,
	updateSmsSettings,
} from "../../../api/communications/smsApi";

import type { SmsNumber, SmsUsageMessage, UpdateSmsSettingsPayload, SmsSettings } from "../../../types/communications/smsTypes";
const { Text, Paragraph } = Typography;

const MESSAGES_PER_PAGE = 25;

/** The registry purpose whose number patients actually see and reply to. */
const NOTIFICATIONS_PURPOSE = "notifications";

/**
 * Recipients are patients, so their numbers are masked to the last three digits — enough to
 * reconcile "did this go to the right person" against a record the clinic already holds,
 * without putting a readable patient mobile number on a settings screen.
 *
 * The clinic's OWN numbers (the Numbers section) are deliberately NOT masked: a clinic that
 * cannot read its own number back cannot tell a patient which number to text.
 */
const maskRecipient = (phone?: string | null): string => {
	if (!phone) return "—";
	const digits = phone.replace(/\D/g, "");
	if (digits.length <= 3) return "•••";
	return `••• ••• ${digits.slice(-3)}`;
};

/** `appointment_reminders` → `Appointment reminders`. Purposes are backend enum-ish strings. */
const humanisePurpose = (purpose: string): string => {
	const spaced = purpose.replace(/[_-]+/g, " ").trim();
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * The purchase relay answers `502 { "error": "..." }` when admin is unreachable or
 * unconfigured — a shape `extractValidationErrors` does not know (it handles Laravel's
 * `errors`/`message` and FastAPI's `detail`). Without this branch a dead admin system would
 * surface as "An unknown error occurred", which reads like a client bug rather than the
 * operational fact it is.
 */
const extractRelayError = (error: unknown): string => {
	if (axios.isAxiosError(error)) {
		const data = error.response?.data as { error?: string } | undefined;
		if (typeof data?.error === "string" && data.error.length > 0) {
			return data.error;
		}
	}
	return extractValidationErrors(error).join(" ");
};

const sameEmails = (a: string[] = [], b: string[] = []): boolean =>
	a.length === b.length && a.every((value, index) => value === b[index]);

const SmsConfiguration: React.FC = () => {
	const queryClient = useQueryClient();
	const { showSuccess } = useNotifications();

	const [settingsForm] = Form.useForm<{ low_balance_threshold: number; alert_emails: string[] }>();
	const [buyForm] = Form.useForm<{ credits_requested: number; note?: string }>();

	const [settingsError, setSettingsError] = useState<string | null>(null);
	const [buyOpen, setBuyOpen] = useState(false);
	const [buyError, setBuyError] = useState<string | null>(null);
	const [messagePage, setMessagePage] = useState(1);

	const { data: credits } = useQuery({
		queryKey: ["sms-credits"],
		queryFn: fetchSmsCredits,
		retry: false,
	});

	const { data: numbers, isLoading: numbersLoading } = useQuery({
		queryKey: ["sms-numbers"],
		queryFn: fetchSmsNumbers,
		retry: false,
	});

	const { data: settings } = useQuery({
		queryKey: ["sms-settings"],
		queryFn: fetchSmsSettings,
		retry: false,
	});

	const { data: messages, isLoading: messagesLoading } = useQuery({
		queryKey: ["sms-messages", messagePage],
		queryFn: () => fetchSmsMessages(messagePage, MESSAGES_PER_PAGE),
		retry: false,
	});

	// The form mirrors the server row; it is never the source of truth for it.
	useEffect(() => {
		if (!settings) return;
		settingsForm.setFieldsValue({
			low_balance_threshold: settings.low_balance_threshold,
			alert_emails: settings.alert_emails ?? [],
		});
	}, [settings, settingsForm]);

	const saveSettings = useMutation<SmsSettings, unknown, UpdateSmsSettingsPayload>({
		mutationFn: updateSmsSettings,
		onSuccess: () => {
			setSettingsError(null);
			showSuccess("SMS settings saved");
			queryClient.invalidateQueries({ queryKey: ["sms-settings"] });
			queryClient.invalidateQueries({ queryKey: ["sms-credits"] });
		},
		onError: (error) => {
			// The server owns email validation; surface exactly what it said (a 422 names the
			// offending address) rather than a generic "save failed".
			setSettingsError(extractValidationErrors(error).join(" "));
		},
	});

	const buyCredits = useMutation({
		mutationFn: requestSmsCredits,
		onError: (error) => setBuyError(extractRelayError(error)),
	});

	const handleSaveSettings = async () => {
		try {
			const values = await settingsForm.validateFields();
			setSettingsError(null);

			// The endpoint is partial by design — sending a field we did not change would let a
			// stale form clobber a value someone else just set.
			const payload: UpdateSmsSettingsPayload = {};
			if (values.low_balance_threshold !== settings?.low_balance_threshold) {
				payload.low_balance_threshold = values.low_balance_threshold;
			}
			if (!sameEmails(values.alert_emails ?? [], settings?.alert_emails ?? [])) {
				payload.alert_emails = values.alert_emails ?? [];
			}

			if (Object.keys(payload).length === 0) {
				showSuccess("No changes to save");
				return;
			}

			saveSettings.mutate(payload);
		} catch (error: any) {
			// antd rejects with { errorFields } when the form itself is invalid, having already
			// rendered its per-field messages. Anything else is real and is surfaced.
			if (error?.errorFields) return;
			setSettingsError(extractValidationErrors(error).join(" "));
		}
	};

	const openBuyModal = () => {
		buyCredits.reset();
		buyForm.resetFields();
		setBuyError(null);
		setBuyOpen(true);
	};

	const handleRequestCredits = async () => {
		try {
			const values = await buyForm.validateFields();
			setBuyError(null);
			buyCredits.mutate({ credits_requested: values.credits_requested, note: values.note });
		} catch (error: any) {
			if (error?.errorFields) return;
			setBuyError(extractRelayError(error));
		}
	};

	const notificationsNumber = useMemo(
		() => (numbers ?? []).find((n) => n.purpose === NOTIFICATIONS_PURPOSE && n.is_active),
		[numbers],
	);

	const numberColumns: ColumnsType<SmsNumber> = [
		{
			title: "Purpose",
			dataIndex: "purpose",
			key: "purpose",
			width: 200,
			render: (purpose: string) => <span style={{ fontWeight: 600 }}>{humanisePurpose(purpose)}</span>,
		},
		{
			title: "Number",
			dataIndex: "msisdn",
			key: "msisdn",
			width: 200,
		},
		{
			title: "Label",
			dataIndex: "label",
			key: "label",
			render: (label: string | null) => label || <Text type="secondary">—</Text>,
		},
		{
			title: "Status",
			dataIndex: "is_active",
			key: "is_active",
			width: 110,
			render: (isActive: boolean) => <StatusTag status={isActive ? "active" : "inactive"} />,
		},
	];

	const messageColumns: ColumnsType<SmsUsageMessage> = [
		{
			title: "Recipient",
			dataIndex: "recipient_phone",
			key: "recipient_phone",
			width: 160,
			render: (phone: string | null) => maskRecipient(phone),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 130,
			render: (status: string) => <StatusTag status={status} />,
		},
		{
			title: "Segments",
			dataIndex: "segments",
			key: "segments",
			width: 100,
			align: "center" as const,
			render: (segments: number | null) => segments ?? 0,
		},
		{
			title: "Credits",
			dataIndex: "credits_charged",
			key: "credits_charged",
			width: 100,
			align: "center" as const,
			render: (charged: number | null) => charged ?? 0,
		},
		{
			title: "Sent",
			dataIndex: "created_at",
			key: "created_at",
			width: 180,
			render: (value: string) => (
				<Text type="secondary">{value ? dayjs(value).format("D MMM YYYY HH:mm") : "—"}</Text>
			),
		},
	];

	const requested = buyCredits.data;

	return (
		<Space direction="vertical" size={16} style={{ width: "100%" }}>
			{/* ── 1. Balance ─────────────────────────────────────────────────── */}
			<SectionCard
				title="SMS credits"
				subtitle="Credits are consumed as messages are sent — one credit per message segment"
				extra={
					<Button
						type="primary"
						icon={<ShoppingCartOutlined />}
						onClick={openBuyModal}
						data-testid="sms-buy-credits-btn"
					>
						Buy credits
					</Button>
				}
			>
				<div data-testid="sms-balance">
					<Statistic
						title="Credits available"
						value={credits?.balance ?? 0}
						valueStyle={{ color: credits?.below_threshold ? "var(--mg-warning)" : "var(--mg-primary)" }}
					/>
				</div>

				{credits?.below_threshold && (
					<div data-testid="sms-low-balance-warning" style={{ marginTop: 16 }}>
						<Alert
							type="warning"
							showIcon
							message="Your SMS credit balance is low"
							description={`You have ${credits.balance} credits left, at or below your low-balance threshold of ${credits.low_balance_threshold}. Buy credits to keep appointment reminders and clinic messages sending.`}
						/>
					</div>
				)}

				{(credits?.deferred_count ?? 0) > 0 && (
					<div data-testid="sms-deferred-notice" style={{ marginTop: 16 }}>
						<Alert
							type="info"
							showIcon
							message={`${credits?.deferred_count} message${credits?.deferred_count === 1 ? "" : "s"} waiting to send`}
							description="These messages were held because there were not enough credits to send them. Nothing has been lost or cancelled — they will send automatically once you top up your credits."
						/>
					</div>
				)}
			</SectionCard>

			{/* ── 2. Numbers ─────────────────────────────────────────────────── */}
			<SectionCard
				title="Your SMS numbers"
				subtitle="Provisioned and maintained by MyGene operations — contact support to add or change a number"
			>
				{notificationsNumber && (
					<div data-testid="sms-notifications-number" style={{ marginBottom: 16 }}>
						<Alert
							type="info"
							showIcon
							icon={<MessageOutlined />}
							message={`Patient notification number: ${notificationsNumber.msisdn}`}
							description="This is the number your patients see when your clinic messages them, and patients can reply to it — their replies come back to your clinic."
						/>
					</div>
				)}

				<DataTable
					columns={numberColumns}
					dataSource={numbers ?? []}
					rowKey="msisdn"
					loading={numbersLoading}
					pagination={false}
					emptyTitle="No SMS numbers provisioned"
					emptyDescription="Contact support to have an SMS number set up for your clinic"
					bare
				/>
			</SectionCard>

			{/* ── 3. Settings ────────────────────────────────────────────────── */}
			<SectionCard
				title="SMS settings"
				subtitle="Choose when to be warned about a low balance and who to warn"
				extra={
					<Button
						type="primary"
						icon={<SaveOutlined />}
						onClick={handleSaveSettings}
						loading={saveSettings.isPending}
						data-testid="sms-settings-save"
					>
						Save
					</Button>
				}
			>
				{settingsError && (
					<div data-testid="sms-settings-error" style={{ marginBottom: 16 }}>
						<Alert type="error" showIcon message="Settings not saved" description={settingsError} />
					</div>
				)}

				<Form form={settingsForm} layout="vertical">
					<Form.Item
						label="Low balance threshold"
						name="low_balance_threshold"
						extra="Warn when the balance falls to this many credits or fewer. Set to 0 to never warn."
						rules={[{ required: true, message: "Enter a threshold (0 to never warn)" }]}
					>
						<InputNumber min={0} style={{ width: 220 }} data-testid="sms-threshold-input" />
					</Form.Item>

					<Form.Item
						label="Low balance alert recipients"
						name="alert_emails"
						extra="These addresses are emailed when the balance drops to the threshold. Press Enter after each address."
					>
						<Select
							mode="tags"
							open={false}
							tokenSeparators={[",", " "]}
							placeholder="alerts@yourclinic.com.au"
							style={{ width: "100%", maxWidth: 520 }}
							data-testid="sms-alert-emails"
						/>
					</Form.Item>
				</Form>

				{/*
				  There is no sender / "from number" field here on purpose — the backend has no
				  such field, and numbers come from the ops-provisioned registry above.
				*/}
				<Text type="secondary" style={{ fontSize: 12 }}>
					The number your messages are sent from is set by MyGene operations and cannot be changed here.
				</Text>
			</SectionCard>

			{/* ── 4. Usage ───────────────────────────────────────────────────── */}
			<SectionCard
				title="Recent messages"
				subtitle="Delivery and credit usage for messages your clinic has sent"
			>
				<Paragraph type="secondary" style={{ fontSize: 12 }}>
					Patient numbers are masked. Message content is not stored for reporting and is never shown here.
				</Paragraph>

				<DataTable
					columns={messageColumns}
					dataSource={messages?.data ?? []}
					rowKey="id"
					loading={messagesLoading}
					total={messages?.total ?? 0}
					currentPage={messagePage}
					pageSize={MESSAGES_PER_PAGE}
					onPageChange={(page) => setMessagePage(page)}
					emptyTitle="No messages sent yet"
					emptyDescription="Messages your clinic sends will be listed here"
					bare
				/>
			</SectionCard>

			{/* ── Buy credits ────────────────────────────────────────────────── */}
			<Modal
				title="Request SMS credits"
				open={buyOpen}
				onCancel={() => setBuyOpen(false)}
				footer={
					requested
						? [
								<Button key="close" type="primary" onClick={() => setBuyOpen(false)}>
									Close
								</Button>,
						  ]
						: [
								<Button key="cancel" onClick={() => setBuyOpen(false)}>
									Cancel
								</Button>,
								<Button
									key="submit"
									type="primary"
									loading={buyCredits.isPending}
									onClick={handleRequestCredits}
									data-testid="sms-buy-credits-submit"
								>
									Send request
								</Button>,
						  ]
				}
				destroyOnClose
			>
				{requested ? (
					<div data-testid="sms-buy-credits-pending">
						<Alert
							type="success"
							showIcon
							message="Request sent"
							description={`Your request for ${requested.credits_requested} credits is now with the MyGene admin team. No payment has been taken. An administrator will review it and apply the credits, and your balance will update here once they do.`}
						/>
						<div style={{ marginTop: 12 }}>
							<Text type="secondary" style={{ marginRight: 8 }}>
								Status
							</Text>
							<StatusTag status={requested.status || "pending"} />
						</div>
					</div>
				) : (
					<>
						{buyError && (
							<div data-testid="sms-buy-credits-error" style={{ marginBottom: 16 }}>
								<Alert type="error" showIcon message="Request not sent" description={buyError} />
							</div>
						)}

						<Alert
							type="info"
							showIcon
							style={{ marginBottom: 16 }}
							message="This raises a request — it does not take payment"
							description="No payment is taken now and credits are not added straight away. An administrator reviews every request and applies the credits, and your balance updates here once they do."
						/>

						<Form form={buyForm} layout="vertical" initialValues={{ credits_requested: 1000 }}>
							<Form.Item
								label="Credits requested"
								name="credits_requested"
								rules={[{ required: true, message: "Enter how many credits you need" }]}
							>
								<InputNumber min={1} style={{ width: 220 }} data-testid="sms-credits-requested-input" />
							</Form.Item>

							<Form.Item label="Note for the admin team (optional)" name="note">
								<Input.TextArea
									rows={3}
									maxLength={1000}
									placeholder="e.g. Running low before the winter clinic push"
								/>
							</Form.Item>
						</Form>
					</>
				)}
			</Modal>
		</Space>
	);
};

export default SmsConfiguration;
